import { useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useLoadingContext } from '../../App';
import { useEnterButtonEventListner } from '../../hooks';
import * as S from './ConfirmPaymentButton.styles';
import { SafePaymentsBadge } from '../safePaymentsBadge';
import { checkIfImageOrDefault } from '../../utils';
import { PaymentMethodChangeStatus, usePaymentMethodContext } from '../../contexts';

interface Props {
  name?: string;
  id?: string;
  customText?: string;
  onClick?: () => Promise<void> | void;
  formId?: string;
  size?: S.ConfirmPaymentButtonSize;
  paymentPageLogoImageUrl?: string;
  onBeforeSubmit?: () => void;
}

export const ConfirmPaymentButton = ({
  size = 'big',
  customText,
  formId,
  id = 'btn-pay',
  name,
  onClick,
  paymentPageLogoImageUrl,
  onBeforeSubmit,
}: Props) => {
  const { isLoading } = useLoadingContext();
  const { t } = useTranslation(['npp']);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const { status } = usePaymentMethodContext();

  const confirm = () => {
    onBeforeSubmit?.(); // Clear error message before submitting
    onClick?.();
    buttonRef.current?.blur();
  };

  useEnterButtonEventListner(() => buttonRef.current?.click());
  const isProcessing = useMemo(
    () => isLoading || status === PaymentMethodChangeStatus.Loading,
    [isLoading, status]
  );

  return (
    <>
      <S.Root
        id={id}
        type="submit"
        className="confirm-payment-button"
        name={name}
        disabled={isProcessing}
        onClick={confirm}
        ref={buttonRef}
        form={formId}
        btnSize={size}
        data-clarity-unmask="True"
      >
        {isProcessing
          ? `${t('npp:general.processing')}...`
          : (customText ?? `${t('npp:general.continue')}`)}
      </S.Root>
      <SafePaymentsBadge logoUrl={checkIfImageOrDefault(paymentPageLogoImageUrl)} />
    </>
  );
};
